// MessageDisplay.js
import React, {
    useRef,
    useEffect,
    useMemo,
    useState,
    useCallback,
    useImperativeHandle,
    forwardRef,
} from 'react';
import {
    Box,
    VStack,
    Text,
    Alert,
    AlertIcon,
    Center,
    Flex,
    Button,
    Icon,
    Fade,
} from '@chakra-ui/react';
import { MdArrowDownward } from 'react-icons/md';
import MessageItem from './MessageItem';
import StreamingCursor from './StreamingCursor';

// How close to the bottom (px) counts as "at the bottom".
// Slightly larger gives a more forgiving feel without being grabby.
const AUTOSCROLL_THRESHOLD = 100;

const MessageDisplay = React.memo(forwardRef(({
    messages,
    error,
    isLoading,
    onDuplicateConversation,
    currentConversationId,
    currentModel,
}, ref) => {
    const scrollContainerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const prevConversationIdRef = useRef(currentConversationId);

    // Single source of truth — a ref so no renders are caused by scroll checks.
    const shouldAutoScrollRef = useRef(true);

    // Tracks whether the *user* is actively scrolling (via wheel/touch).
    // This lets us distinguish user intent from programmatic scrolls.
    const userInteractingRef = useRef(false);

    const [showScrollButton, setShowScrollButton] = useState(false);

    // ── Message split ──────────────────────────────────────────────────────────

    const { stableMessages, streamingMessage } = useMemo(() => {
        if (!messages?.length) {
            return { stableMessages: [], streamingMessage: null };
        }
        const last = messages[messages.length - 1];
        if (last.isStreaming) {
            return {
                stableMessages: messages.slice(0, -1),
                streamingMessage: last,
            };
        }
        return { stableMessages: messages, streamingMessage: null };
    }, [messages]);

    const isStreaming = !!streamingMessage;
    const showThinkingCursor = isLoading && !isStreaming;
    const thinkingModelName = currentModel || 'Assistant';

    // ── Distance helper ────────────────────────────────────────────────────────

    const getDistanceFromBottom = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return 0;
        return el.scrollHeight - el.scrollTop - el.clientHeight;
    }, []);

    // ── Core scroll-to-bottom primitive ────────────────────────────────────────
    // Uses direct scrollTop assignment instead of scrollIntoView.
    // This is smoother during streaming and never scrolls ancestor elements.

    const jumpToBottom = useCallback((smooth = false) => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTo({
            top: el.scrollHeight,
            behavior: smooth ? 'smooth' : 'auto',
        });
    }, []);

    // ── Public scroll-to-bottom (button / conversation switch) ─────────────────

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        shouldAutoScrollRef.current = true;
        userInteractingRef.current = false;
        setShowScrollButton(false);
        jumpToBottom(behavior === 'smooth');
    }, [jumpToBottom]);

    // ── Imperative handle ──────────────────────────────────────────────────────
    // Exposes scrollIfAtBottom() to the parent (App.js), called during streaming.

    useImperativeHandle(ref, () => ({
        scrollIfAtBottom: () => {
            // Only follow the stream if autoscroll is still armed AND
            // the user isn't mid-gesture scrolling away.
            if (shouldAutoScrollRef.current && !userInteractingRef.current) {
                jumpToBottom(false);
            }
        },
        scrollToBottom: (behavior = 'auto') => {
            shouldAutoScrollRef.current = true;
            userInteractingRef.current = false;
            setShowScrollButton(false);
            jumpToBottom(behavior === 'smooth');
        },
    }), [jumpToBottom]);

    // ── User-intent detection (wheel + touch) ──────────────────────────────────
    // THE KEY FIX: when the user scrolls UP with an explicit gesture,
    // immediately disarm autoscroll. We don't wait for the scroll event,
    // which can be defeated by content growing at the bottom.

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        // Wheel: deltaY < 0 means scrolling up
        const onWheel = (e) => {
            if (e.deltaY < 0) {
                // User is scrolling up — disarm immediately
                shouldAutoScrollRef.current = false;
                userInteractingRef.current = true;
            }
        };

        // Touch: track finger movement direction
        let touchStartY = 0;
        const onTouchStart = (e) => {
            touchStartY = e.touches[0].clientY;
            userInteractingRef.current = true;
        };
        const onTouchMove = (e) => {
            const currentY = e.touches[0].clientY;
            // Finger moving DOWN on screen = scrolling content UP
            if (currentY > touchStartY) {
                shouldAutoScrollRef.current = false;
            }
        };
        const onTouchEnd = () => {
            // After the gesture, re-evaluate based on final position.
            // If they flicked back to the bottom, re-arm autoscroll.
            const distance = getDistanceFromBottom();
            if (distance <= AUTOSCROLL_THRESHOLD) {
                shouldAutoScrollRef.current = true;
            }
            userInteractingRef.current = false;
        };

        container.addEventListener('wheel', onWheel, { passive: true });
        container.addEventListener('touchstart', onTouchStart, { passive: true });
        container.addEventListener('touchmove', onTouchMove, { passive: true });
        container.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            container.removeEventListener('wheel', onWheel);
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchmove', onTouchMove);
            container.removeEventListener('touchend', onTouchEnd);
        };
    }, [getDistanceFromBottom]);

    // ── Scroll position listener (re-arm + button visibility only) ─────────────
    // This NO LONGER disarms autoscroll on its own — that's the wheel/touch
    // handler's job. It only RE-ARMS when the user returns to the bottom,
    // and controls the "New messages" button.

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const onScroll = () => {
            const distance = getDistanceFromBottom();
            const isNearBottom = distance <= AUTOSCROLL_THRESHOLD;

            // Re-arm autoscroll only when the user has scrolled back to bottom
            // by their own action. We never disarm here (avoids the race
            // condition where growing content pushes us back into "near bottom").
            if (isNearBottom) {
                shouldAutoScrollRef.current = true;
            }

            setShowScrollButton(!isNearBottom && (isStreaming || isLoading));
        };

        container.addEventListener('scroll', onScroll, { passive: true });
        return () => container.removeEventListener('scroll', onScroll);
    }, [getDistanceFromBottom, isStreaming, isLoading]);

    // ── Conversation switch ────────────────────────────────────────────────────

    useEffect(() => {
        if (prevConversationIdRef.current === currentConversationId) return;
        prevConversationIdRef.current = currentConversationId;

        shouldAutoScrollRef.current = true;
        userInteractingRef.current = false;
        setShowScrollButton(false);

        // Double rAF: first frame commits the new list, second lays it out.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                jumpToBottom(false);
            });
        });
    }, [currentConversationId, jumpToBottom]);

    // ── Non-streaming message additions ────────────────────────────────────────
    // Handles the user message appearing and the final message landing.

    const prevMessageCountRef = useRef(messages?.length ?? 0);
    useEffect(() => {
        const count = messages?.length ?? 0;
        const prevCount = prevMessageCountRef.current;
        prevMessageCountRef.current = count;

        if (count !== prevCount && count > 0 && shouldAutoScrollRef.current) {
            requestAnimationFrame(() => {
                jumpToBottom(false);
            });
        }
    }, [messages, jumpToBottom]);

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <Box
            ref={scrollContainerRef}
            flex="1"
            overflowY="auto"
            overflowX="hidden"
            // Extra top padding on mobile clears the fixed hamburger button
            pt={{ base: '60px', md: 4 }}
            px={{ base: 3, md: 4 }}
            pb={{ base: 3, md: 4 }}
            bg="chat.bg"
            position="relative"
            sx={{
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
            }}
        >
            {(stableMessages.length > 0 || streamingMessage) ? (
                <Box maxW="4xl" mx="auto" w="full">
                    <VStack spacing={4} align="stretch">
                        {stableMessages.map((message, messageIndex) => (
                            <MessageItem
                                key={message.id || messageIndex}
                                message={message}
                                messageIndex={messageIndex}
                                onDuplicateConversation={onDuplicateConversation}
                                onScrollToBottom={() => scrollToBottom('smooth')}
                                isStreaming={false}
                            />
                        ))}

                        {streamingMessage && (
                            <MessageItem
                                key={`streaming-${streamingMessage.id}`}
                                message={streamingMessage}
                                messageIndex={stableMessages.length}
                                onDuplicateConversation={onDuplicateConversation}
                                onScrollToBottom={() => scrollToBottom('smooth')}
                                isStreaming
                            />
                        )}
                    </VStack>

                    <Box ref={messagesEndRef} h="1px" />
                </Box>
            ) : (
                <Center h="full">
                    <Text color="text.muted" fontSize="lg" fontWeight="500">
                        No messages to display. Start a conversation!
                    </Text>
                </Center>
            )}

            {error && (
                <Box maxW="4xl" mx="auto" mt={4}>
                    <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <Text>{error}</Text>
                    </Alert>
                </Box>
            )}

            {showThinkingCursor && (
                <Box maxW="4xl" mx="auto" mt={4}>
                    <Box pl={0} pr={1} pt={2} pb={2}>
                        <Flex align="center">
                            <StreamingCursor />
                            <Text color="text.secondary" fontSize="sm" fontStyle="italic">
                                {thinkingModelName} is thinking
                            </Text>
                        </Flex>
                    </Box>
                </Box>
            )}

            <Fade in={showScrollButton}>
                <Button
                    position="fixed"
                    bottom={{ base: '160px', md: '120px' }}
                    right={{ base: '16px', md: '40px' }}
                    size="sm"
                    colorScheme="brand"
                    borderRadius="full"
                    boxShadow="lg"
                    leftIcon={<Icon as={MdArrowDownward} />}
                    onClick={() => scrollToBottom('smooth')}
                    zIndex="sticky"
                    _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'xl',
                    }}
                    transition="all 0.2s"
                >
                    New messages
                </Button>
            </Fade>
        </Box>
    );
}), (prevProps, nextProps) =>
    prevProps.messages === nextProps.messages &&
    prevProps.error === nextProps.error &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.currentConversationId === nextProps.currentConversationId &&
    prevProps.currentModel === nextProps.currentModel
);

MessageDisplay.displayName = 'MessageDisplay';
export default MessageDisplay;